import { fetchPartnerAllDataRegionsRows } from 'clickNgo/entities/partner/api/fetchPartnerAllDataRegionsRows'
import { IForm } from 'core-components/classes/models/IForm'
import { IDataProviderFilter, IFilter, Operator } from 'core-components/types'
import { CrudInnerStorageFetcher } from 'core/types/CrudInnerStorageFetcher'
import Logger from 'core/utils/Logger'

type TDataSet = { formId: number; code: string; audience?: boolean }

const P360_PAGE_PATH = /\/page\/p360(\/|$)/

const isP360Page = () => P360_PAGE_PATH.test(window.location.pathname)

const collectConditions = (group: IDataProviderFilter, conditions: IFilter[] = []): IFilter[] => {
  group.filters.forEach((item) => {
    if ('filters' in item) {
      collectConditions(item, conditions)
    } else {
      conditions.push(item)
    }
  })

  return conditions
}

/* The procedure takes the partner id only, so it serves just a read whose one condition is PartnerID = id. */
const getOnlyPartnerId = (filter: IDataProviderFilter): string | null => {
  const [condition, ...others] = collectConditions(filter)

  if (
    !condition ||
    others.length ||
    condition.field !== 'PartnerID' ||
    condition.operator !== Operator.EQUAL
  ) {
    return null
  }

  const { value } = condition
  return typeof value === 'string' || typeof value === 'number' ? String(value) || null : null
}

/** The PartnerAllDataRegions data set. On the p360 page a read of one partner by PartnerID comes from
 *  spPartnerAllDataRegions instead; every other read goes to the data set as before. */
export class PartnerAllDataRegionsFetcher<T extends IForm> extends CrudInnerStorageFetcher<T> {
  constructor(
    private readonly dataSetKeyField: string,
    private readonly dataSet: TDataSet
  ) {
    super(dataSetKeyField, dataSet)

    /* The parent's read is an arrow property: it is kept before the swap and serves every other read. */
    const readFromDataSet = this.read

    this.read = async (
      query,
      filter,
      sort,
      paging,
      keyField,
      fetchCallBack,
      hierarchyField,
      ...rest
    ) => {
      const partnerId = isP360Page() ? getOnlyPartnerId(filter) : null

      if (!partnerId) {
        return readFromDataSet(
          query,
          filter,
          sort,
          paging,
          keyField,
          fetchCallBack,
          hierarchyField,
          ...rest
        )
      }

      try {
        const rows = (await fetchPartnerAllDataRegionsRows(partnerId)) as T[]
        const items = fetchCallBack ? await fetchCallBack(rows) : rows
        const result = { items, total: items.length }

        await this.saveToStore(query, result, hierarchyField)

        return result
      } catch (error) {
        Logger.error('spPartnerAllDataRegions:', error)

        return { items: [], total: 0, error: String(error) }
      }
    }
  }

  public clone(): PartnerAllDataRegionsFetcher<T> {
    return new PartnerAllDataRegionsFetcher<T>(this.dataSetKeyField, this.dataSet)
  }
}
